import type { CrudFilters } from '@refinedev/core';
import type { UserFilterValues } from '../components/filters/UserFilters';
import type { CallColor } from '../components/CallColorFilters';

export interface ListFilterState {
  search: string;
  drawer: UserFilterValues;
  callColor: CallColor | null;
  // Exclude users with this call colour (e.g. hide "Rasvo qilinganlar" / brown).
  excludeCallColor?: CallColor | null;
  // Drivers list also tracks a separate verification tab next to the title.
  // Pass undefined when the page doesn't have one.
  verification?: boolean | null;
  // Drivers list also has a license-status pill row (samozanyatyy/IP).
  license?: boolean | null;
  // Preferred-language pill (UZ / RU / EN). Shared by /users /drivers /passengers.
  language?: 'UZ' | 'RU' | 'EN' | null;
  // Inline status select on the list pages.
  status?: string;
  // Role select shown on the all-users list (absent on /drivers and /passengers).
  role?: string;
}

const pushIfDefined = (
  arr: CrudFilters,
  field: string,
  operator: any,
  value: any,
) => {
  if (value === undefined || value === null || value === '') return;
  arr.push({ field, operator, value });
};

/**
 * Build a refine `CrudFilters` array from the consolidated filter state for
 * a list page. Always rebuilds from scratch — caller passes this to
 * `setFilters(arr, 'replace')` so refine fully replaces non-permanent
 * filters and dropdown clearing actually removes the field.
 */
export function buildUserListFilters(s: ListFilterState): CrudFilters {
  const arr: CrudFilters = [];

  pushIfDefined(arr, 'search', 'contains', s.search.trim() || undefined);
  pushIfDefined(arr, 'role', 'eq', s.role);
  pushIfDefined(arr, 'status', 'eq', s.status);
  pushIfDefined(arr, 'callIconColor', 'eq', s.callColor);
  pushIfDefined(arr, 'excludeCallColor', 'eq', s.excludeCallColor);

  if (s.verification === true || s.verification === false) {
    arr.push({
      field: 'isDriverVerified',
      operator: 'eq',
      value: String(s.verification),
    });
  }

  if (s.license === true || s.license === false) {
    arr.push({
      field: 'isLicensed',
      operator: 'eq',
      value: String(s.license),
    });
  }

  if (s.language) {
    arr.push({
      field: 'preferredLang',
      operator: 'eq',
      value: s.language,
    });
  }

  const d = s.drawer;
  pushIfDefined(arr, 'phone', 'contains', d.phone);
  // Drawer's isDriverVerified takes precedence over the sidebar tab when set.
  if (d.isDriverVerified !== undefined) {
    arr.push({
      field: 'isDriverVerified',
      operator: 'eq',
      value: String(d.isDriverVerified),
    });
  }
  pushIfDefined(arr, 'minTrips', 'gte', d.minTrips);
  pushIfDefined(arr, 'maxTrips', 'lte', d.maxTrips);
  pushIfDefined(arr, 'minRating', 'gte', d.minRating);
  pushIfDefined(arr, 'maxRating', 'lte', d.maxRating);
  pushIfDefined(arr, 'registeredFrom', 'gte', d.registeredFrom);
  pushIfDefined(arr, 'registeredTo', 'lte', d.registeredTo);
  if (d.blockedOnly !== undefined) {
    arr.push({ field: 'blockedOnly', operator: 'eq', value: String(d.blockedOnly) });
  }
  if (d.hasCompletedTrips !== undefined) {
    arr.push({
      field: 'hasCompletedTrips',
      operator: 'eq',
      value: String(d.hasCompletedTrips),
    });
  }
  if (d.hasVehicle !== undefined) {
    arr.push({ field: 'hasVehicle', operator: 'eq', value: String(d.hasVehicle) });
  }
  // Vehicle tariffs → comma-joined list the backend splits (array_contains OR).
  if (Array.isArray(d.tariffs) && d.tariffs.length > 0) {
    arr.push({ field: 'tariffs', operator: 'in', value: d.tariffs.join(',') });
  }
  pushIfDefined(arr, 'homeRegionId', 'eq', d.homeRegionId);
  pushIfDefined(arr, 'regularPickupRegionId', 'eq', d.regularPickupRegionId);
  pushIfDefined(arr, 'regularDestinationRegionId', 'eq', d.regularDestinationRegionId);

  return arr;
}

/** Count of meaningful drawer filter fields, for the "N" badge on the
 *  filter button. Excludes search/role/status which have their own UI. */
export function countDrawerFilters(d: UserFilterValues): number {
  return Object.entries(d).filter(([k, v]) => {
    if (v === undefined || v === null || v === '') return false;
    // Empty multi-select arrays (e.g. tariffs) aren't active filters.
    if (Array.isArray(v) && v.length === 0) return false;
    // Exclude page-level fields that aren't part of the drawer surface.
    if (k === 'search' || k === 'role' || k === 'status') return false;
    return true;
  }).length;
}
